import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Baby,
  Download,
  FileText,
  Info,
  Plus,
  Scale,
  Trash2,
} from 'lucide-react';
import api from '../../lib/api';
import { money } from '../../lib/money';
import type { Case } from '../../types';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  IconButton,
  Input,
  Select,
  Skeleton,
  SkeletonTable,
  Tag,
  type TagTone,
} from '../ui';
import SourcePageViewer from '../SourcePageViewer';
import IncomeDiscoveryPanel, { type EvidenceTxn } from '../IncomeDiscoveryPanel';
import RequirementsSpine from '../RequirementsSpine';
import {
  useAcceptIncomeStream,
  useAddChild,
  useDismissIncomeStream,
  useChildSupportParams,
  useChildSupportProfile,
  useChildSupportRuns,
  useChildSupportWorksheet,
  useChildren,
  useDeleteChild,
  useDeleteChildSupportRun,
  useGenerateChildSupportPacket,
  useSaveChildSupportProfile,
  type ChildSupportWorksheet,
  type IncomeStream,
  type PartyWorksheet,
  type ResourceLine,
} from '../../hooks/useChildSupport';

interface Props {
  caseId: string;
  caseData: Case;
}

const fmt = (v: number | null | undefined) => money(v);

/** Court documents write dates out; the UI should match so nothing reads as raw data. */
const fmtDate = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

/** Lines the attorney can type a figure into. Wage income comes from documents. */
const EDITABLE_CODES = [
  'wage_income',
  'self_employment_income',
  'interest_dividends_royalties',
  'net_rental_income',
  'other_income',
  'deemed_income',
];

/**
 * A text/number field that commits on blur instead of on every keystroke.
 *
 * Committing per keystroke fired a PUT and a full worksheet refetch per
 * character — typing one sentence into the variance-reason box was roughly forty
 * round trips. Selects and checkboxes still commit immediately, because a single
 * click is already the whole edit.
 */
function DraftInput({
  label,
  value,
  onCommit,
  type = 'text',
  ...rest
}: {
  label?: string;
  value: string | number | null | undefined;
  onCommit: (raw: string) => void;
  type?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'>) {
  const external = value == null ? '' : String(value);
  const [draft, setDraft] = useState(external);
  const [focused, setFocused] = useState(false);

  // Accept external changes (another field's save re-fetched the profile) but
  // never yank the value out from under someone mid-edit.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- deliberate draft↔server sync; pre-dates the sweep, behavior preserved
    if (!focused) setDraft(external);
  }, [external, focused]);

  return (
    <Input
      {...rest}
      label={label}
      type={type}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        if (draft !== external) onCommit(draft);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') {
          setDraft(external);
          (e.target as HTMLInputElement).blur();
        }
      }}
    />
  );
}

const numOrNull = (raw: string) => (raw.trim() === '' ? null : Number(raw));

function SectionNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs text-ink-soft leading-relaxed flex gap-2">
      <Info size={12} className="shrink-0 mt-(--space-hair) text-info" />
      <span>{children}</span>
    </p>
  );
}

function RoleBadge({ role }: { role: string }) {
  const tone: TagTone =
    role === 'obligor' ? 'opposing' : role === 'obligee' ? 'client' : 'neutral';
  return <Tag tone={tone}>{role === 'unassigned' ? 'Not assigned' : role.toUpperCase()}</Tag>;
}

// ---------------------------------------------------------------------------
// Resource worksheet for one party
// ---------------------------------------------------------------------------

function PartyCard({
  party,
  onOverride,
  confirmedNone,
  onConfirmNone,
  onConfirmAllRemaining,
}: {
  party: PartyWorksheet;
  overrides: Record<string, { amount: number; reason?: string }>;
  onOverride: (code: string, amount: number | null, reason: string) => void;
  confirmedNone: string[];
  onConfirmNone: (code: string, isNone: boolean) => void;
  onConfirmAllRemaining: () => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [draftAmount, setDraftAmount] = useState('');
  const [draftReason, setDraftReason] = useState('');

  const startEdit = (line: ResourceLine) => {
    setEditing(line.code);
    setDraftAmount(line.amount != null ? String(line.amount) : '');
    setDraftReason(line.override_reason || '');
  };

  const commit = (code: string) => {
    const parsed = draftAmount.trim() === '' ? null : Number(draftAmount);
    if (parsed != null && Number.isNaN(parsed)) return;
    onOverride(code, parsed, draftReason.trim());
    setEditing(null);
  };

  const nr = party.net_resources;
  const unreviewed = party.lines.filter((l) => l.status === 'not_reviewed');

  return (
    <Card>
      <CardHeader className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CardTitle>{party.display_name}</CardTitle>
          <RoleBadge role={party.role} />
        </div>
        {nr && (
          <div className="text-right">
            <div className="t-eyebrow">Monthly net resources</div>
            <div className="t-num-hero text-green-900">{fmt(nr.net_resources)}</div>
            {unreviewed.length > 0 && (
              <div className="text-xs text-danger mt-1">
                Understated — {unreviewed.length} categor
                {unreviewed.length === 1 ? 'y' : 'ies'} not reviewed
              </div>
            )}
          </div>
        )}
      </CardHeader>

      {unreviewed.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-clay-200 bg-clay-50 px-3 py-2">
          <span className="text-xs text-clay-ink">
            Unreviewed categories are excluded from the total, not counted as $0.00. Enter a
            figure or confirm the category is empty.
          </span>
          <Button size="sm" variant="ghost" onClick={onConfirmAllRemaining}>
            Confirm all {unreviewed.length} as none
          </Button>
        </div>
      )}

      <div className="overflow-x-auto">
        {/* eslint-disable-next-line no-restricted-syntax -- editable resource worksheet with deduction/total rows; doesn't fit DataTable; restyled in place */}
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-border">
              <th className="py-2 pr-3 t-eyebrow text-left">Resource</th>
              <th className="py-2 pr-3 t-eyebrow text-left">Authority</th>
              <th className="py-2 pr-3 t-eyebrow text-left">Evidence</th>
              <th className="py-2 pr-3 t-eyebrow text-right">Monthly</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {party.lines.map((line) => (
              <tr key={line.code} className="border-b border-border-subtle align-top">
                <td className="py-2 pr-3 text-ink">
                  {line.label}
                  <div className="text-xs text-ink-faint mt-1">{line.source}</div>
                  {line.override_reason && (
                    <div className="text-xs text-clay-ink mt-1">
                      Reason: {line.override_reason}
                    </div>
                  )}
                  {line.note && (
                    <div className="text-xs text-warn mt-1">{line.note}</div>
                  )}
                </td>
                <td className="py-2 pr-3 text-xs text-ink-faint whitespace-nowrap">{line.statute}</td>
                <td className="py-2 pr-3 whitespace-nowrap">
                  {line.exhibit_citation ? (
                    <Tag tone="neutral" title="Source document in the exhibit index below">
                      {line.exhibit_citation}
                    </Tag>
                  ) : (
                    <span className="text-xs text-ink-faint">—</span>
                  )}
                </td>
                <td className="py-2 pr-3 text-right whitespace-nowrap">
                  {editing === line.code ? (
                    <div className="space-y-1 w-40 ml-auto">
                      <Input
                        type="number"
                        step="0.01"
                        value={draftAmount}
                        placeholder="Monthly amount"
                        onChange={(e) => setDraftAmount(e.target.value)}
                      />
                      <Input
                        value={draftReason}
                        placeholder="Reason (recommended)"
                        onChange={(e) => setDraftReason(e.target.value)}
                      />
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" onClick={() => commit(line.code)}>
                          Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : line.status === 'not_reviewed' ? (
                    <span className="text-danger font-medium">Not reviewed</span>
                  ) : (
                    <span className={line.status === 'confirmed_none' ? 'text-ink-soft' : 'text-ink'}>
                      {fmt(line.amount)}
                    </span>
                  )}
                </td>
                <td className="py-2 text-right whitespace-nowrap">
                  {EDITABLE_CODES.includes(line.code) && editing !== line.code && (
                    <div className="flex items-center gap-2 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => startEdit(line)}>
                        {line.status === 'established' ? 'Edit' : 'Set'}
                      </Button>
                      <label
                        className="flex items-center gap-1 text-xs text-ink-soft cursor-pointer"
                        title="Confirm this category is empty, so it counts as $0.00"
                      >
                        {/* eslint-disable-next-line no-restricted-syntax -- native checkbox: no ui/Checkbox primitive exists yet */}
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-clay-300 accent-green-700"
                          checked={confirmedNone.includes(line.code)}
                          onChange={(e) => onConfirmNone(line.code, e.target.checked)}
                        />
                        None
                      </label>
                    </div>
                  )}
                </td>
              </tr>
            ))}

            {nr && (
              <>
                {[
                  ['Less: social security and Medicare taxes', nr.social_security_taxes, '§ 154.062(d)(1)'],
                  ['Less: federal income tax (single filer, standard deduction)', nr.federal_income_tax, '§ 154.062(d)(2)'],
                  ['Less: union dues', nr.union_dues, '§ 154.062(d)(4)'],
                  ["Less: child's health/dental coverage ordered by the court", nr.child_coverage_deductions, '§ 154.062(d)(5)'],
                  ['Less: nondiscretionary retirement', nr.nondiscretionary_retirement, '§ 154.062(d)(6)'],
                ].map(([label, value, statute]) => (
                  <tr key={label as string} className="border-b border-border-subtle">
                    <td className="py-2 pr-3 text-ink-soft">{label as string}</td>
                    <td className="py-2 pr-3 text-xs text-ink-faint whitespace-nowrap">
                      {statute as string}
                    </td>
                    <td />
                    <td className="py-2 pr-3 text-right text-ink-soft whitespace-nowrap">
                      {fmt(value as number)}
                    </td>
                    <td />
                  </tr>
                ))}
                <tr className="font-semibold text-green-900">
                  <td className="py-2 pr-3">Monthly net resources</td>
                  <td className="py-2 pr-3 text-xs font-normal text-ink-faint">§ 154.062</td>
                  <td />
                  <td className="py-2 pr-3 text-right">{fmt(nr.net_resources)}</td>
                  <td />
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-3">
        <SectionNote>
          The federal income tax line is computed for a single person claiming one personal
          exemption and the standard deduction as § 154.062(d)(2) requires — it is deliberately
          not the amount withheld on the pay stub.
        </SectionNote>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Children
// ---------------------------------------------------------------------------

function ChildrenCard({ caseId }: { caseId: string }) {
  const { data: children = [] } = useChildren(caseId);
  const addChild = useAddChild(caseId);
  const deleteChild = useDeleteChild(caseId);
  const [initials, setInitials] = useState('');
  const [dob, setDob] = useState('');
  const [beforeCourt, setBeforeCourt] = useState(true);

  const submit = () => {
    if (!initials.trim()) return;
    addChild.mutate(
      {
        initials: initials.trim(),
        date_of_birth: dob || null,
        is_before_court: beforeCourt,
      },
      {
        onSuccess: () => {
          setInitials('');
          setDob('');
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Baby size={16} className="text-green-700" />
          <CardTitle>Children</CardTitle>
        </div>
      </CardHeader>

      {children.length === 0 ? (
        <p className="text-sm text-ink-soft mb-4">
          No children entered. Support, the step-down schedule and the order's caption all key
          off the individual children.
        </p>
      ) : (
        <ul className="mb-4 divide-y divide-border-subtle">
          {children.map((c) => (
            <li key={c.id} className="py-2 flex items-center justify-between gap-3">
              <div>
                <span className="text-sm font-medium text-ink">{c.initials || c.full_name}</span>
                <span className="text-xs text-ink-faint ml-2">
                  {c.date_of_birth ? `DOB ${c.date_of_birth}` : 'No date of birth'}
                  {!c.is_before_court && ' · not before this court'}
                  {c.is_disabled_adult && ' · adult disabled child (§ 154.302)'}
                </span>
              </div>
              <IconButton
                icon={Trash2}
                size="xs"
                variant="ghost"
                aria-label="Remove child"
                className="text-ink-faint hover:text-danger hover:bg-danger-soft"
                onClick={() => c.id && deleteChild.mutate(c.id)}
              />
            </li>
          ))}
        </ul>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
        <Input
          label="Initials"
          value={initials}
          placeholder="A.B."
          onChange={(e) => setInitials(e.target.value)}
        />
        <Input
          label="Date of birth"
          type="date"
          value={dob}
          onChange={(e) => setDob(e.target.value)}
        />
        <Button size="lg" icon={Plus} onClick={submit} loading={addChild.isPending}>
          Add
        </Button>
      </div>
      <label className="flex items-center gap-2 mt-3 text-sm text-ink-soft">
        {/* eslint-disable-next-line no-restricted-syntax -- native checkbox: no ui/Checkbox primitive exists yet */}
        <input
          type="checkbox"
          checked={beforeCourt}
          onChange={(e) => setBeforeCourt(e.target.checked)}
          className="w-4 h-4 rounded border-clay-300 accent-green-700"
        />
        Before this court
      </label>
      <div className="mt-3">
        <SectionNote>
          Filings identify children by initials. Children the obligor supports in another
          household are counted separately below and drive §§ 154.128 / 154.129.
        </SectionNote>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------

export default function ChildSupportTab({ caseId, caseData }: Props) {
  const { data: worksheet, isLoading } = useChildSupportWorksheet(caseId);
  const { data: profile = {} } = useChildSupportProfile(caseId);
  const { data: params } = useChildSupportParams();
  const { data: runs = [] } = useChildSupportRuns(caseId);
  const saveProfile = useSaveChildSupportProfile(caseId);
  const acceptStream = useAcceptIncomeStream(caseId);
  const dismissStream = useDismissIncomeStream(caseId);
  const generate = useGenerateChildSupportPacket(caseId);
  const [viewer, setViewer] = useState<{
    statementId: number;
    pageNumber: number;
    sourceFile: string;
  } | null>(null);
  const [busyStream, setBusyStream] = useState<string | null>(null);
  const deleteRun = useDeleteChildSupportRun(caseId);
  const [genError, setGenError] = useState<string | null>(null);

  const patch = (p: Parameters<typeof saveProfile.mutate>[0]) => saveProfile.mutate(p);

  const overrides = useMemo(
    () => (profile.resource_overrides || {}) as Record<string, Record<string, { amount: number; reason?: string }>>,
    [profile.resource_overrides],
  );

  const setOverride = (party: string, code: string, amount: number | null, reason: string) => {
    const next = { ...overrides, [party]: { ...(overrides[party] || {}) } };
    if (amount == null) {
      delete next[party][code];
    } else {
      next[party][code] = { amount, reason };
    }
    patch({ resource_overrides: next });
  };

  const confirmedNone = useMemo(
    () => (profile.confirmed_none_lines || {}) as Record<string, string[]>,
    [profile.confirmed_none_lines],
  );

  const setConfirmedNone = (party: string, codes: string[]) =>
    patch({ confirmed_none_lines: { ...confirmedNone, [party]: codes } });

  const toggleConfirmedNone = (party: string, code: string, isNone: boolean) => {
    const current = new Set(confirmedNone[party] || []);
    if (isNone) current.add(code);
    else current.delete(code);
    setConfirmedNone(party, [...current]);
  };

  const handleAcceptStream = (party: string, stream: IncomeStream) => {
    setBusyStream(stream.key);
    acceptStream.mutate(
      {
        party,
        stream_key: stream.key,
        line_code: stream.line_code,
        monthly_amount: stream.monthly_average,
        reason: `${stream.occurrences} recurring deposits from ${stream.label}, ${stream.first_date} to ${stream.last_date}`,
        transaction_ids: stream.transaction_ids,
        statement_ids: stream.statement_ids,
      },
      { onSettled: () => setBusyStream(null) },
    );
  };

  const handleDismissStream = (party: string, stream: IncomeStream) => {
    setBusyStream(stream.key);
    dismissStream.mutate(
      { party, stream_key: stream.key, reason: 'Reviewed and not treated as income' },
      { onSettled: () => setBusyStream(null) },
    );
  };

  const runGenerate = () => {
    setGenError(null);
    generate.mutate(undefined, {
      onError: (err: unknown) => {
        const detail = (err as { response?: { data?: { detail?: { message?: string } | string } } })
          ?.response?.data?.detail;
        setGenError(
          typeof detail === 'string' ? detail : detail?.message || 'Could not generate the packet.',
        );
      },
    });
  };

  const download = (runId: number, kind: string) => {
    window.open(
      `${api.defaults.baseURL}/cases/${caseId}/child-support/runs/${runId}/download/${kind}`,
      '_blank',
    );
  };

  if (isLoading || !worksheet) {
    return (
      <div className="space-y-6 max-w-5xl">
        <Skeleton variant="rect" height={140} className="w-full" />
        <Skeleton variant="rect" height={96} className="w-full" />
        <SkeletonTable rows={6} columns={4} />
      </div>
    );
  }

  const w: ChildSupportWorksheet = worksheet;
  const selected = w.selected;
  const readiness = w.readiness;
  // The spine already represents missing inputs; only analytical warnings remain here.
  const SPINE_COVERED = new Set([
    'unknown_obligor_resources',
    'unreviewed_obligee_resources',
    'no_medical_support_election',
    'no_dental_support_election',
    'unreviewed_income_streams',
    'income_evidence',
  ]);
  const analysisNotes = readiness.warnings.filter((x) => !SPINE_COVERED.has(x.code));
  const hasMultipleHouseholds = w.children_outside > 0;

  const partyOptions = [
    { value: '', label: 'Select the obligor…' },
    { value: 'client', label: caseData.client_name || 'Client' },
    { value: 'opposing', label: caseData.opposing_party_name || 'Opposing party' },
  ];

  return (
    <div className="space-y-6 max-w-5xl">
      {/* headline */}
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Scale size={16} className="text-green-700" />
              <CardTitle>Texas guideline child support</CardTitle>
            </div>
            <p className="text-sm text-ink-soft max-w-xl">
              Tex. Fam. Code ch. 154. Computed as of {fmtDate(w.as_of)} using {w.tax_year} federal tax
              parameters. Presumptive cap on monthly net resources: {fmt(w.cap)}.
            </p>
          </div>
          <div className="text-right">
            <div className="t-eyebrow">Total monthly obligation</div>
            <div className="t-num-display text-green-900">
              {fmt(w.total_monthly_obligation)}
            </div>
            {selected && (
              <div className="text-xs text-ink-soft mt-1">
                {fmt(w.base_support)} periodic support
                {w.total_monthly_obligation !== w.base_support &&
                  ` + ${fmt(w.total_monthly_obligation - w.base_support)} additional`}
              </div>
            )}
          </div>
        </div>

        {selected && (
          <div className="mt-4 pt-4 border-t border-border-subtle grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-xs text-ink-faint">Children before the court</div>
              <div className="font-medium text-ink">{w.children_before_court}</div>
            </div>
            <div>
              <div className="text-xs text-ink-faint">Percentage applied</div>
              <div className="font-medium text-ink">{selected.percentage_display}</div>
            </div>
            <div>
              <div className="text-xs text-ink-faint">Net resources applied</div>
              <div className="font-medium text-ink">{fmt(selected.applied_net_resources)}</div>
            </div>
            <div>
              <div className="text-xs text-ink-faint">Method</div>
              <div className="font-medium text-ink">§ {selected.method}</div>
            </div>
          </div>
        )}

        {selected?.notes.map((note) => (
          <p key={note} className="mt-3 text-xs text-clay-ink bg-clay-50 border border-clay-200 rounded px-3 py-2">
            {note}
          </p>
        ))}
      </Card>

      {/* the readiness spine: have / found / missing, with routed requests */}
      <RequirementsSpine
        requirements={w.requirements}
        requests={w.document_requests}
        blockingCount={readiness.blocking.length}
      />

      {/* analytical findings the spine doesn't carry (discrepancies, cap, low-income) */}
      {analysisNotes.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Analysis notes</CardTitle>
          </CardHeader>
          <ul className="space-y-2">
            {analysisNotes.map((item, i) => (
              <li key={`${item.code}-${i}`} className="flex gap-2 text-sm">
                {item.code === 'income_discrepancy' || item.code === 'undocumented_payroll_deposits' ? (
                  <AlertTriangle size={16} className="text-danger shrink-0 mt-(--space-hair)" />
                ) : (
                  <Info size={16} className="text-warn shrink-0 mt-(--space-hair)" />
                )}
                <span className="text-ink-soft">{item.message}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* setup */}
      <Card>
        <CardHeader>
          <CardTitle>Setup</CardTitle>
        </CardHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label="Who is the obligor?"
            options={partyOptions}
            value={profile.obligor_party || ''}
            onChange={(e) => patch({ obligor_party: e.target.value || null })}
          />
          <Select
            label="Order this will go into"
            options={[
              { value: 'temporary', label: 'Temporary Orders' },
              { value: 'final_decree', label: 'Final Decree of Divorce' },
              { value: 'modification', label: 'Order Modifying Prior Order' },
            ]}
            value={profile.order_vessel || 'temporary'}
            onChange={(e) => patch({ order_vessel: e.target.value })}
          />
          <Select
            label="Style of the case"
            options={[
              { value: 'divorce', label: 'Divorce with children (marriage + SAPCR)' },
              { value: 'sapcr', label: 'SAPCR only (no marriage in the style)' },
            ]}
            value={profile.suit_type || w.suit_type}
            onChange={(e) => patch({ suit_type: e.target.value })}
          />
          <DraftInput
            label="Obligor's children in another household"
            type="number"
            min={0}
            value={profile.obligor_children_outside ?? 0}
            onCommit={(raw) => patch({ obligor_children_outside: Number(raw || 0) })}
          />
          <Select
            label="Tax year"
            options={Object.entries(params?.tax_years || {}).map(([year, cfg]) => ({
              value: year,
              label: cfg.label,
            }))}
            value={String(profile.tax_year || w.tax_year)}
            onChange={(e) => patch({ tax_year: Number(e.target.value) })}
          />
          <DraftInput
            label="First payment due"
            type="date"
            value={profile.first_payment_date || ''}
            onCommit={(raw) => patch({ first_payment_date: raw || null })}
          />
        </div>
        <div className="mt-3">
          <SectionNote>
            Obligor and obligee are not the same axis as client and opposing — the client is
            frequently the obligor.
          </SectionNote>
        </div>
      </Card>

      <ChildrenCard caseId={caseId} />

      {/* worksheets, each preceded by what the deposits show for that party */}
      {(['client', 'opposing'] as const).map((key) => {
        const party = w.parties[key];
        if (!party) return null;
        return (
          <div key={`${key}-block`} className="space-y-6">
          {party.role !== 'unassigned' && (
            <IncomeDiscoveryPanel
              displayName={party.display_name}
              streams={party.candidates}
              comparison={party.income_comparison}
              isMaterial={party.comparison_is_material}
              documentedSource={
                party.lines.find((l) => l.code === 'wage_income')?.source
              }
              busyKey={busyStream}
              onAccept={(stream) => handleAcceptStream(key, stream)}
              onDismiss={(stream) => handleDismissStream(key, stream)}
              onViewSource={setViewer}
              evidenceFor={(stream) =>
                ((stream as IncomeStream & { transactions?: EvidenceTxn[] }).transactions ?? [])
              }
            />
          )}
          <PartyCard
            party={party}
            overrides={overrides[key] || {}}
            onOverride={(code, amount, reason) => setOverride(key, code, amount, reason)}
            confirmedNone={confirmedNone[key] || []}
            onConfirmNone={(code, isNone) => toggleConfirmedNone(key, code, isNone)}
            onConfirmAllRemaining={() =>
              setConfirmedNone(key, [
                ...new Set([
                  ...(confirmedNone[key] || []),
                  ...party.lines.filter((l) => l.status === 'not_reviewed').map((l) => l.code),
                ]),
              ])
            }
          />
          </div>
        );
      })}

      {/* multiple households */}
      {hasMultipleHouseholds && (
        <Card>
          <CardHeader>
            <CardTitle>Children in more than one household</CardTitle>
          </CardHeader>
          <p className="text-sm text-ink-soft mb-4">
            §§ 154.128 and 154.129 are alternative methods — § 154.129 applies in lieu of the
            § 154.128 computation. They need not agree; choose the one you are advancing.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              ['154.128', 'Credit method', w.alternative_154_128],
              ['154.129', 'Adjusted guidelines table', w.alternative_154_129],
            ].map(([method, label, result]) => {
              const r = result as ChildSupportWorksheet['guideline'];
              const active = profile.multiple_household_method === method;
              return (
                // eslint-disable-next-line no-restricted-syntax -- selectable method card: no ui primitive fits this layout
                <button
                  key={method as string}
                  type="button"
                  onClick={() => patch({ multiple_household_method: method as string })}
                  className={`text-left rounded-lg border p-4 transition-colors ${
                    active
                      ? 'border-green-700 bg-green-50'
                      : 'border-border hover:bg-cream-50'
                  }`}
                >
                  <div className="t-eyebrow">
                    § {method as string}
                  </div>
                  <div className="text-sm font-medium text-ink">{label as string}</div>
                  <div className="t-num-hero text-green-900 mt-1">
                    {fmt(r?.presumptive_amount)}
                  </div>
                  <div className="text-xs text-ink-soft">{r?.percentage_display}</div>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {/* medical + dental */}
      <Card>
        <CardHeader>
          <CardTitle>Medical and dental support</CardTitle>
        </CardHeader>
        <p className="text-sm text-ink-soft mb-4">
          Medical support is mandatory under § 154.181 in any order setting periodic support, and
          dental support is separately mandatory under §§ 154.1815 and 154.1825. When the obligee
          carries the coverage, the obligor reimburses the child's share as{' '}
          <em>additional child support</em> — it adds to the order rather than reducing net
          resources.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Select
            label="Health insurance carried by"
            options={[
              { value: '', label: 'Not recorded' },
              { value: 'obligor', label: 'Obligor' },
              { value: 'obligee', label: 'Obligee' },
              { value: 'none', label: 'Not available at reasonable cost' },
            ]}
            value={profile.health_carried_by || ''}
            onChange={(e) => patch({ health_carried_by: e.target.value || null })}
          />
          <DraftInput
            label="Total health premium / mo"
            type="number"
            step="0.01"
            value={profile.health_premium_total ?? ''}
            onCommit={(raw) => patch({ health_premium_total: numOrNull(raw) })}
          />
          <DraftInput
            label="Minor dependents on that plan"
            type="number"
            min={1}
            value={profile.health_plan_dependents ?? 1}
            onCommit={(raw) => patch({ health_plan_dependents: Number(raw || 1) })}
          />
          <Select
            label="Dental insurance carried by"
            options={[
              { value: '', label: 'Not recorded' },
              { value: 'obligor', label: 'Obligor' },
              { value: 'obligee', label: 'Obligee' },
              { value: 'none', label: 'Not available at reasonable cost' },
            ]}
            value={profile.dental_carried_by || ''}
            onChange={(e) => patch({ dental_carried_by: e.target.value || null })}
          />
          <DraftInput
            label="Total dental premium / mo"
            type="number"
            step="0.01"
            value={profile.dental_premium_total ?? ''}
            onCommit={(raw) => patch({ dental_premium_total: numOrNull(raw) })}
          />
          <DraftInput
            label="Minor dependents on that plan"
            type="number"
            min={1}
            value={profile.dental_plan_dependents ?? 1}
            onCommit={(raw) => patch({ dental_plan_dependents: Number(raw || 1) })}
          />
          <DraftInput
            label="Health insurer"
            placeholder="e.g. BCBS of Texas"
            value={profile.health_insurer ?? ''}
            onCommit={(raw) => patch({ health_insurer: raw || null })}
          />
          <DraftInput
            label="Health policy number"
            value={profile.health_policy_number ?? ''}
            onCommit={(raw) => patch({ health_policy_number: raw || null })}
          />
          <DraftInput
            label="Uninsured expense split — obligor's share (%)"
            type="number"
            min={0}
            max={100}
            step="0.5"
            placeholder="50"
            value={profile.uninsured_split_obligor_pct ?? ''}
            onCommit={(raw) => patch({ uninsured_split_obligor_pct: numOrNull(raw) })}
          />
          <DraftInput
            label="Dental insurer"
            placeholder="e.g. Delta Dental"
            value={profile.dental_insurer ?? ''}
            onCommit={(raw) => patch({ dental_insurer: raw || null })}
          />
          <DraftInput
            label="Dental policy number"
            value={profile.dental_policy_number ?? ''}
            onCommit={(raw) => patch({ dental_policy_number: raw || null })}
          />
        </div>
        <div className="mt-3">
          <SectionNote>
            §§ 154.181(b) and 154.1815(c) require the insurer, policy number and premium to be
            disclosed before a hearing on temporary orders. Leave the expense split empty to
            keep the conventional 50/50 flagged for review in the draft order.
          </SectionNote>
        </div>

        <div className="mt-4 pt-4 border-t border-border-subtle grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-xs text-ink-faint">Health reimbursement (§ 154.182(b-1))</div>
            <div className="font-medium text-ink">
              {fmt(w.medical_support.health_premium_additional_support)}
            </div>
          </div>
          <div>
            <div className="text-xs text-ink-faint">Dental reimbursement (§ 154.1825(d))</div>
            <div className="font-medium text-ink">
              {fmt(w.medical_support.dental_premium_additional_support)}
            </div>
          </div>
          <div>
            <div className="text-xs text-ink-faint">
              Cash medical (ceiling {fmt(w.medical_support.cash_medical_ceiling)})
            </div>
            <div className="font-medium text-ink">{fmt(w.medical_support.cash_medical_support)}</div>
          </div>
        </div>
      </Card>

      {/* above the cap */}
      {selected?.over_cap && (
        <Card>
          <CardHeader>
            <CardTitle>Support above the cap — § 154.126</CardTitle>
          </CardHeader>
          <p className="text-sm text-ink-soft mb-4">
            Net resources exceed the presumptive cap, so the guideline stops at{' '}
            {fmt(selected.presumptive_amount)}. Anything above that requires proof of the child's
            needs: the entire presumptive award is subtracted from the proven total needs, and the
            remainder is allocated between the parties. The obligor may never be ordered to pay
            more than the greater of the presumptive amount or 100% of proven needs.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DraftInput
              label="Proven total needs of the child / mo"
              type="number"
              step="0.01"
              value={profile.proven_needs_monthly ?? ''}
              onCommit={(raw) => patch({ proven_needs_monthly: numOrNull(raw) })}
            />
            <DraftInput
              label="Share of unmet need allocated to the obligor (0–1)"
              type="number"
              step="0.05"
              min={0}
              max={1}
              value={profile.obligor_allocation_share ?? 1}
              onCommit={(raw) => patch({ obligor_allocation_share: Number(raw || 0) })}
            />
          </div>
          {w.above_cap && (
            <div className="mt-4 pt-4 border-t border-border-subtle text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-ink-soft">Needs remaining after the presumptive award</span>
                <span className="text-ink">{fmt(w.above_cap.unmet_needs)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-soft">Allocated to the obligor</span>
                <span className="text-ink">{fmt(w.above_cap.obligor_share_of_unmet)}</span>
              </div>
              <div className="flex justify-between font-semibold text-green-900">
                <span>Maximum that may be ordered</span>
                <span>{fmt(w.above_cap.maximum_orderable)}</span>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* step-down */}
      {w.step_down.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Step-down schedule</CardTitle>
          </CardHeader>
          <p className="text-sm text-ink-soft mb-3">
            Support does not silently drop when a child ages out — the order recites the reduced
            amount at each stage.
          </p>
          {/* eslint-disable-next-line no-restricted-syntax -- three-row in-card summary; restyled in place, DataTable chrome would double-border it */}
          <table className="w-full text-sm">
            <tbody>
              {w.step_down.map((s) => (
                <tr key={s.effective_after} className="border-b border-border-subtle">
                  <td className="py-2 pr-3 text-ink-soft">{s.effective_after}</td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap text-ink-faint">
                    {s.children_remaining} remaining
                  </td>
                  <td className="py-2 text-right whitespace-nowrap font-medium text-ink">
                    {fmt(s.monthly_amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* variance + findings */}
      <Card>
        <CardHeader>
          <CardTitle>Variance and § 154.130 findings</CardTitle>
        </CardHeader>
        <p className="text-sm text-ink-soft mb-4">{w.findings_154_130.required_reason}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <DraftInput
            label="Amount ordered, if it varies from the guideline"
            type="number"
            step="0.01"
            value={profile.variance_amount ?? ''}
            onCommit={(raw) => patch({ variance_amount: numOrNull(raw) })}
          />
          <DraftInput
            label="Specific reasons for the variance (§ 154.130(b)(4))"
            value={profile.variance_reason ?? ''}
            onCommit={(raw) => patch({ variance_reason: raw || null })}
          />
        </div>
        <div className="mt-4 pt-4 border-t border-border-subtle text-sm space-y-1">
          <div className="flex justify-between">
            <span className="text-ink-soft">Obligor's monthly net resources</span>
            <span className="text-ink">{fmt(w.findings_154_130.obligor_net_resources)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-soft">
              Obligee's monthly net resources (only if evidence was offered — § 154.130(c))
            </span>
            <span className="text-ink">{fmt(w.findings_154_130.obligee_net_resources)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-ink-soft">Percentage applied</span>
            <span className="text-ink">{w.findings_154_130.percentage_applied || '—'}</span>
          </div>
        </div>
      </Card>

      {/* generate */}
      <Card>
        <CardHeader className="flex items-center justify-between gap-3">
          <CardTitle>Filing packet</CardTitle>
          <Button
            icon={FileText}
            onClick={runGenerate}
            loading={generate.isPending}
            disabled={!readiness.ready}
          >
            Generate packet
          </Button>
        </CardHeader>
        <p className="text-sm text-ink-soft">
          Generates the child support worksheet (exhibit-formatted), the order provisions for the
          selected vessel, and the § 154.130 findings. Every document is a draft for attorney
          review.
        </p>
        {!readiness.ready && (
          <p className="mt-2 text-sm text-danger">
            Resolve the blocking items above before generating.
          </p>
        )}
        {genError && <p className="mt-2 text-sm text-danger">{genError}</p>}

        {runs.length > 0 && (
          <ul className="mt-4 divide-y divide-border-subtle">
            {runs.map((run) => (
              <li key={run.id} className="py-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium text-ink">{run.title}</div>
                    <div className="text-xs text-ink-faint">
                      {fmt(run.total_monthly_obligation)} / mo · {run.tax_year} tax parameters ·
                      cap {fmt(run.cap_amount)} · params {run.params_version}
                    </div>
                  </div>
                  <IconButton
                    icon={Trash2}
                    size="xs"
                    variant="ghost"
                    aria-label="Delete packet"
                    className="text-ink-faint hover:text-danger hover:bg-danger-soft"
                    onClick={() => deleteRun.mutate(run.id)}
                  />
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {run.documents.map((doc) => (
                    <Button
                      key={doc.kind}
                      size="sm"
                      variant="secondary"
                      icon={Download}
                      onClick={() => download(run.id, doc.kind)}
                    >
                      {doc.filename}
                    </Button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {w.exhibit_index.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Index of exhibits</CardTitle>
          </CardHeader>
          <p className="text-sm text-ink-soft mb-4">
            Every figure in the worksheet is keyed to the document it came from. The same index
            is printed at the end of the generated worksheet.
          </p>
          {/* eslint-disable-next-line no-restricted-syntax -- small in-card index; restyled in place, DataTable chrome would double-border it */}
          <table className="w-full text-sm">
            <tbody>
              {w.exhibit_index.map((ex) => (
                <tr key={ex.letter} className="border-b border-border-subtle">
                  <td className="py-2 pr-3 w-16 align-top">
                    <Tag tone="neutral">{ex.letter}</Tag>
                  </td>
                  <td className="py-2 pr-3 text-ink align-top">
                    {ex.label}
                    {ex.pages.length > 0 && (
                      <span className="text-ink-faint"> · p. {ex.pages.join(', ')}</span>
                    )}
                  </td>
                  <td className="py-2 text-right text-xs text-ink-faint align-top whitespace-nowrap">
                    {ex.filename || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {viewer && (
        <SourcePageViewer
          statementId={viewer.statementId}
          pageNumber={viewer.pageNumber}
          sourceFile={viewer.sourceFile}
          isOpen={!!viewer}
          onClose={() => setViewer(null)}
        />
      )}
    </div>
  );
}
